import { deleteOwnedMedia, uploadOwnedMedia } from '../../util/ownedMedia';

export async function uploadCreativeProfilePicture({
  creative,
  imageData,
  uploadMedia = uploadOwnedMedia,
}) {
  if (!creative?.id) {
    throw new Error('Creative-Profil konnte nicht zugeordnet werden.');
  }

  const file = imageData?.file;
  if (!file) {
    throw new Error('Profilbild konnte nicht zugeordnet werden.');
  }

  const uploadResult = await uploadMedia({
    file,
    entityType: 'CREATIVE',
    entityId: creative.id,
    mediaKind: 'IMAGE',
    slot: 'profile',
    fileName: file.name || 'profile.jpg',
  });

  return uploadResult.key;
}

export function buildCreativeProfileUpdateData({
  creative,
  formValues,
  profilePictureKey,
}) {
  const updateData = {
    id: creative.id,
    displayName: formValues.displayName,
    jobTitle: formValues.jobTitle,
    description: formValues.description,
    skills: formValues.skills,
  };

  if (profilePictureKey) {
    updateData.profilePicture = profilePictureKey;
  }

  return updateData;
}

export function hasCreativeProfileNonStatusChanges({
  creative,
  formValues,
  profilePictureKey,
  originalStudioId,
}) {
  return (
    formValues.displayName !== (creative.displayName || '') ||
    formValues.jobTitle !== (creative.jobTitle || '') ||
    formValues.description !== (creative.description || '') ||
    JSON.stringify([...(formValues.skills || [])].sort()) !== JSON.stringify([...(creative.skills || [])].sort()) ||
    profilePictureKey !== creative?.profilePicture ||
    (creative?.studios?.items?.[0]?.studioID || null) !== originalStudioId
  );
}

export async function cleanupCreativeProfileUpload({
  key,
  deleteMedia = deleteOwnedMedia,
}) {
  if (!key) return null;

  return deleteMedia({
    key,
    entityType: 'CREATIVE',
    mediaKind: 'IMAGE',
  });
}

export async function saveCreativeProfileWithMedia({
  creative,
  formValues,
  originalStudioId,
  intendedStatus = null,
  updateCreative,
  publishCreative,
  uploadProfilePicture = uploadCreativeProfilePicture,
  cleanupUpload = cleanupCreativeProfileUpload,
}) {
  let uploadedProfilePictureKey = null;
  let profilePictureKey = creative?.profilePicture;
  let profileUpdateCompleted = false;

  try {
    if (formValues.profilePicture?.file) {
      uploadedProfilePictureKey = await uploadProfilePicture({
        creative,
        imageData: formValues.profilePicture,
      });
      profilePictureKey = uploadedProfilePictureKey;
    }

    if (intendedStatus !== null) {
      if (intendedStatus === 'PUBLISHED') {
        await publishCreative(creative.id);
      } else if (intendedStatus === 'INACTIVE') {
        await updateCreative({
          id: creative.id,
          status: 'INACTIVE',
        });
      }
    }

    const updateData = buildCreativeProfileUpdateData({
      creative,
      formValues,
      profilePictureKey,
    });

    const hasNonStatusChanges = hasCreativeProfileNonStatusChanges({
      creative,
      formValues,
      profilePictureKey,
      originalStudioId,
    });

    if (hasNonStatusChanges) {
      await updateCreative(updateData);
      profileUpdateCompleted = true;
    }

    return {
      hasNonStatusChanges,
      profilePictureKey,
      updateData,
    };
  } catch (error) {
    if (uploadedProfilePictureKey && !profileUpdateCompleted) {
      try {
        await cleanupUpload({ key: uploadedProfilePictureKey });
      } catch (cleanupError) {
        error.cleanupError = cleanupError;
      }
    }
    throw error;
  }
}
